import { AxiosError } from "axios";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Skeleton from "react-loading-skeleton";
import { RootState } from "../../../app/store";
import store from "../../../app/store";
import { setRequestId } from "../../../app/slices/trendSlice";
import { ReactComponent as LeftArrow } from "../../../assets/icons/leftArrow.svg";
import { ReactComponent as Download } from "../../../assets/icons/download.svg";
import { handleTrendCurveDownload } from "../../../utils/trendSidebarGraphUtils/trendCurveDownloadUtils";
import {
  normalizeTrendCurveWindowLabel,
  trendCurveWindows,
  TrendCurveWindowType,
} from "../../../constants/constants";
import {
  TrendCurveChartData,
  TrendItem,
} from "../../../interface/DesignerInterface";
import {
  getActiveTrendReqId,
  getTrendFiltersForTrendCurve,
} from "../../../utils/trendSidebarGraphUtils/graphHelperUtils";
import {
  formatAndSetDataForTrendCurves,
  getCachedTrendCurveChartForWindow,
  getLiveTrendCurveWindowLabel,
  getTrendCurveColorContextKey,
  persistTrendCurvePreferences,
  prefetchTrendCurveWindows,
  TREND_CURVE_LIVE_FILTER_TYPE,
} from "../../../utils/trendSidebarGraphUtils/trendCurvesUtils";
import { setTrendCurveDatasetHighlight } from "../../../utils/trendSidebarGraphUtils/trendCurveChartRenderer";
import Dropdown from "../Dropdown";
import TrendTable from "./TrendCurveTable";
import TrendCurvesGraph from "./TrendCurvesGraph";

interface ExpandViewTrendCurvesProps {
  isSidebarOpen?: boolean;
}

const ExpandViewTrendCurves: React.FC<ExpandViewTrendCurvesProps> = ({
  isSidebarOpen,
}) => {
  const navigate = useNavigate();
  const [curveWindowType, setCurveWindowType] = useState<TrendCurveWindowType>(
    () => getLiveTrendCurveWindowLabel()
  );
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [trendsCurveRawData, setTrendsCurveRawData] = useState<TrendItem[]>([]);
  const [trendsCurveData, setTrendsCurveData] =
    useState<TrendCurveChartData | null>({
      labels: [],
      datasets: [],
    });
  const [selectedTNo, setSelectedTNo] = useState<number | null>(null);
  const [downloadLoading, setDownloadLoading] = useState<boolean>(false);
  const chartRef = useRef<SVGSVGElement | null>(null);
  const pageScrollRef = useRef<HTMLDivElement | null>(null);
  const graphContainerRef = useRef<HTMLElement | null>(null);

  const user = useSelector((state: RootState) => state.user.userInfo);
  const reduxReqId = useSelector(
    (state: RootState) => state.trends.trendsData?.req_id
  );
  const graphRevision = useSelector(
    (state: RootState) => state.trends.graphRevision
  );
  const trendCurveRefreshKey = useSelector(
    (state: RootState) => state.trends.trendsData?.trendCurveRefreshKey ?? 0
  );
  const reqId = reduxReqId || getActiveTrendReqId();

  useEffect(() => {
    const activeReqId = getActiveTrendReqId();
    if (activeReqId && !store.getState().trends.trendsData?.req_id) {
      store.dispatch(setRequestId(activeReqId));
    }
  }, []);

  useEffect(() => {
    setSelectedTNo(null);
  }, [curveWindowType, reduxReqId, trendCurveRefreshKey]);

  useEffect(() => {
    const activeReqId = getActiveTrendReqId();
    if (!activeReqId) {
      setLoading(false);
      setRefreshing(false);
      setTrendsCurveData({ labels: [], datasets: [] });
      setTrendsCurveRawData([]);
      return;
    }

    const cache = store.getState().trends.trendsData?.trendCurveCache;
    const trendFilters = getTrendFiltersForTrendCurve();
    const colorContextKey = getTrendCurveColorContextKey(trendFilters);
    const hasMatchingCache =
      cache &&
      cache.filterType === TREND_CURVE_LIVE_FILTER_TYPE &&
      cache.curveWindowType === curveWindowType &&
      cache.req_id === activeReqId &&
      cache.colorContextKey === colorContextKey;
    const cachedWindowChartData =
      getCachedTrendCurveChartForWindow(curveWindowType);
    const hasExistingData = Boolean(
      trendsCurveData && trendsCurveData.datasets.length > 0
    );

    const loadTrendCurveData = async () => {
      try {
        // Skeleton only on true first load; otherwise keep the current chart
        // visible (stale-while-revalidate) or resolve instantly from cache.
        if (cachedWindowChartData || hasMatchingCache) {
          setLoading(false);
          setRefreshing(false);
        } else if (hasExistingData) {
          setLoading(false);
          setRefreshing(true);
        } else {
          setLoading(true);
        }

        await formatAndSetDataForTrendCurves(
          TREND_CURVE_LIVE_FILTER_TYPE,
          curveWindowType,
          user,
          setTrendsCurveData,
          setTrendsCurveRawData
        );
        prefetchTrendCurveWindows(user, curveWindowType);
      } catch (error) {
        console.error("Error fetching trend curve data", error);
        if ((error as AxiosError)?.response?.status !== 400) {
          toast.error("Error fetching trend curve data");
        }
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    };

    persistTrendCurvePreferences(curveWindowType, activeReqId);
    loadTrendCurveData();
  }, [curveWindowType, reduxReqId, trendCurveRefreshKey, user, graphRevision]);

  const visibleChartData = useMemo((): TrendCurveChartData | null => {
    if (!trendsCurveData) return null;
    if (selectedTNo == null) return trendsCurveData;
    return {
      labels: trendsCurveData.labels,
      datasets: trendsCurveData.datasets.filter(
        dataset => dataset.tNo === selectedTNo
      ),
    };
  }, [trendsCurveData, selectedTNo]);

  const hasChartData = Boolean(
    visibleChartData && visibleChartData.datasets.length > 0
  );

  const handleCurveWindowSelect = (option: string) => {
    const nextWindow = normalizeTrendCurveWindowLabel(option);
    if (nextWindow === curveWindowType) return;
    setCurveWindowType(nextWindow);
    const activeReqId = getActiveTrendReqId();
    if (activeReqId) {
      persistTrendCurvePreferences(nextWindow, activeReqId);
    }
  };

  const handleRowClick = (tNo: number) => {
    setSelectedTNo(prev => (prev === tNo ? null : tNo));
    setTrendCurveDatasetHighlight(chartRef.current, null);
    pageScrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  };

  const panelWidthClass = isSidebarOpen
    ? "w-[calc(100vw-105px)]"
    : "w-[calc(100vw-265px)]";

  return (
    <div
      className={`fixed ml-2 h-[calc(100vh-80px)] bg-white transition-all duration-500 ease-in-out ${panelWidthClass}`}
    >
      <header className="relative z-30 flex items-center justify-between px-4 pt-3 pb-6">
        <div className="flex items-center gap-3">
          <LeftArrow
            className="cursor-pointer text-lg"
            onClick={() => navigate("/designer-page")}
          />
          <h1 className="font-semibold text-secondary">Trend Curve</h1>
        </div>
        <div className="flex items-end gap-5">
          <button
            type="button"
            className="flex items-center gap-2 border border-gray-200 rounded-md px-3 py-1.5 bg-white text-secondary hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed transition"
            disabled={downloadLoading || !hasChartData}
            title="Download Trend Curve Report"
            onClick={() =>
              visibleChartData &&
              handleTrendCurveDownload(
                setDownloadLoading,
                visibleChartData,
                trendsCurveRawData,
                graphContainerRef.current ?? undefined,
                TREND_CURVE_LIVE_FILTER_TYPE,
                curveWindowType
              )
            }
          >
            {downloadLoading ? (
              <span className="inline-block h-4 w-4 rounded-full border-2 border-gray-300 border-t-primary animate-spin" />
            ) : (
              <Download width={20} height={20} fill="#212121" />
            )}
          </button>
          <div className="w-48">
            <div className="mb-0.5 text-sm text-gray-700">Curve Window</div>
            <Dropdown
              label={curveWindowType || "Month"}
              options={[...trendCurveWindows]}
              onSelect={handleCurveWindowSelect}
            />
          </div>
        </div>
      </header>

      <div
        ref={pageScrollRef}
        className="h-[calc(100vh-140px)] overflow-y-auto px-3"
      >
        {!reqId && !loading ? (
          <div className="my-10 text-center text-gray-600">
            Run trend analysis on the designer page first.
          </div>
        ) : loading ? (
          <div className="my-10 flex items-center justify-center">
            <Skeleton height={300} className="w-full" />
          </div>
        ) : hasChartData && visibleChartData && trendsCurveData ? (
          <>
            <section
              ref={graphContainerRef}
              className={`relative z-0 mr-6 bg-white pb-2 pt-1 transition-opacity duration-200 ${
                refreshing ? "opacity-60" : ""
              }`}
            >
              {refreshing ? (
                <div className="absolute right-2 top-2 z-10 flex items-center gap-1.5 text-xs text-gray-500">
                  <span className="inline-block h-3.5 w-3.5 rounded-full border-2 border-gray-300 border-t-primary animate-spin" />
                  Refreshing…
                </div>
              ) : null}
              {selectedTNo != null ? (
                <div className="mb-1 flex items-center justify-between px-1 text-xs text-gray-600">
                  <span>
                    Showing Trend No. <strong>{selectedTNo}</strong> only
                  </span>
                  <button
                    type="button"
                    className="font-medium text-secondary underline"
                    onClick={() => setSelectedTNo(null)}
                  >
                    Show all trends
                  </button>
                </div>
              ) : null}
              <TrendCurvesGraph
                ref={chartRef}
                key={`${TREND_CURVE_LIVE_FILTER_TYPE}-${reqId}`}
                trendCurveData={visibleChartData}
                trendsCurveExpandedView
                fillContainer
                showContributionLabels={selectedTNo != null}
              />
            </section>
            <div className="mt-6">
              <TrendTable
                trendsCurveRawData={trendsCurveRawData}
                curveWindowType={curveWindowType}
                datasets={trendsCurveData.datasets}
                selectedTNo={selectedTNo}
                onRowClick={handleRowClick}
                onRowHover={datasetIndex =>
                  setTrendCurveDatasetHighlight(chartRef.current, datasetIndex)
                }
              />
            </div>
          </>
        ) : (
          <div className="my-10 text-center text-gray-600">
            No Trend Curve Data
          </div>
        )}
      </div>
    </div>
  );
};

export default ExpandViewTrendCurves;
